import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function DepartmentCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      color: "#3b82f6",
      image: null,
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      image: id ? Yup.string().nullable() : Yup.string().required("Image is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("color", values.color);
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.image) {
        formData.append("image", values.image);
      }

      try {
        let response;
        if (id) {
          formData.append("_method", "PUT");
          response = await request.post("departments/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("departments/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(id ? "Department updated successfully!" : "Department created successfully!");
          navigate("/admin/departments");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchDepartment = async () => {
        setLoading(true);
        try {
          const response = await request.get(`departments/${id}`);
          const department = response.data.data;
          formik.setValues({
            title: department.title || "",
            color: department.color || "#3b82f6",
            image: null,
            sort_order: department.sort_order?.toString() || "",
            isActive: department.status ? "Active" : "Inactive",
          });
          setEditImagePreview(imagepathReturn(department.image || null));
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchDepartment();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={id && id !== "new" ? "Edit Department" : "Create New Department"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/departments"),
              }}
              primaryButton={{
                label: id && id !== "new" ? "Update Department" : "Create Department",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="title"
                    label="Title"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="sort_order"
                    label="Sort Order"
                    type="number"
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="color"
                    label="Color"
                    type="color"
                    placeholder="#3b82f6"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <FileUpload
                    name="image"
                    formik={formik}
                    label="Upload Image"
                    editImagePreview={editImagePreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}